import clsx from 'clsx';
import { COUNTRY } from '~globals';
import { Button } from '~components/Button';
import { ComponentLink } from '~components/ComponentLink';
import { Image } from '~components/Image';
import { ImageProvider } from '~components/Image/ImageProvider';
import { useSiteConfig } from '~hooks/useSiteConfig';

import ChevronRight from '../assets/chevron-right.svg';

import * as styles from './CTA.module.css';

import type { ContentfulComponentLinkOrContentfulComponentLinkGroup } from '~components/types';
import type { HeaderCta } from '~models/contentful';

type CTAProps = {
  className?: string;
  headerCta?: HeaderCta;
};

export const CTA: React.FC<CTAProps> = ({ className, headerCta }) => {
  const { headerCtaItems: globalHeaderCtaItems } = useSiteConfig(COUNTRY);

  const CTAItems = getCTAItems({
    globalHeaderCtaItems,
    pageHeaderCtaItems: headerCta?.cta,
    disable: headerCta?.disable,
  });

  if (!CTAItems) return null;

  return (() => {
    switch (CTAItems.__typename) {
      case 'ContentfulComponentLink':
        return (
          <div className={clsx(styles.wrapper, className)}>
            <Button className={styles.button} element={<ComponentLink link={CTAItems} />}>
              {CTAItems.label}
            </Button>
          </div>
        );
      case 'ContentfulComponentLinkGroup':
        return (
          <ImageProvider loading='eager'>
            <div className={clsx(styles.wrapper, className)}>
              <Button className={styles.button} animateOnHover={false}>
                {CTAItems.label}
              </Button>
              <div className={styles.itemsContainer}>
                <div className={styles.itemsContent}>
                  {CTAItems.items.map((item) => (
                    <ComponentLink key={item.label} link={item} className={styles.link}>
                      <Image className={styles.icon} image={item.icon} />
                      <span className={styles.label}>{item.label}</span>
                      <span className={styles.description}>{item.description}</span>
                      <ChevronRight className={styles.chevronRight} />
                    </ComponentLink>
                  ))}
                </div>
              </div>
            </div>
          </ImageProvider>
        );
      default:
        return null;
    }
  })();
};

const getCTAItems = ({
  globalHeaderCtaItems,
  pageHeaderCtaItems,
  disable,
}: {
  globalHeaderCtaItems?: ContentfulComponentLinkOrContentfulComponentLinkGroup | null;
  pageHeaderCtaItems?: ContentfulComponentLinkOrContentfulComponentLinkGroup | null;
  disable?: boolean;
}): ContentfulComponentLinkOrContentfulComponentLinkGroup | null => {
  if (!!disable) return null;
  if (pageHeaderCtaItems) return pageHeaderCtaItems;
  if (globalHeaderCtaItems) return globalHeaderCtaItems;
  return null;
};
